/**
 * ITER158 (Feb 2026): Importación de facturas de compra desde el SRI.
 *
 * Flujo: el usuario pega/sube las claves de acceso de sus facturas recibidas
 * (obtenidas del portal SRI en Línea), el sistema consulta cada una al WS
 * oficial del SRI, descarga el XML autorizado y crea las facturas_compra
 * en estado "importada_sri". El contador luego solo agrega retención + pago
 * + descripción, sin re-tipear datos.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, FileText, CheckCircle2, XCircle, AlertCircle, Info } from "lucide-react";

export default function ImportarSri() {
  const [texto, setTexto] = useState("");
  const [claves, setClaves] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const parsear = async () => {
    setParsing(true);
    try {
      const { data } = await api.post("/compras/sri/parsear-claves", { contenido: texto });
      setClaves(data.claves_acceso || []);
      if ((data.claves_acceso || []).length === 0) {
        toast.error("No se encontraron claves de acceso (49 dígitos) en el texto");
      } else {
        toast.success(`${data.total} clave(s) de acceso detectada(s)`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setParsing(false); }
  };

  const importar = async () => {
    if (claves.length === 0) return toast.error("Primero detecte las claves de acceso");
    setImporting(true);
    setResult(null);
    try {
      const { data } = await api.post("/compras/sri/importar", { claves_acceso: claves });
      setResult(data);
      const msg = `Importación completa: ${data.creadas} nueva(s), ${data.existentes} ya existente(s), ${data.con_errores} con error(es)`;
      if (data.creadas > 0) toast.success(msg);
      else toast(msg);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-sri-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Compras · SRI</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <Download className="inline mr-2 mb-1" size={26} />
          Importar facturas recibidas desde SRI
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">
          Pegue las claves de acceso (49 dígitos) de las facturas que sus proveedores le emitieron.
          El sistema las consultará al SRI y creará las facturas de compra automáticamente.
        </p>
      </div>

      {/* Instrucciones */}
      <div className="rounded-md border border-[#b2c5db] bg-[#eef2f9] p-4 text-sm">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-[#2d4a73] mt-0.5 shrink-0" />
          <div className="text-[#15241d] space-y-1">
            <p><strong>¿Cómo obtener las claves de acceso?</strong></p>
            <ol className="list-decimal list-inside text-[13px] text-[#4a5c53] space-y-0.5">
              <li>Ingrese a <a href="https://srienlinea.sri.gob.ec/sri-en-linea" target="_blank" rel="noreferrer" className="text-[#205c45] underline">SRI en Línea</a> con su RUC y clave.</li>
              <li>Facturación Electrónica → Comprobantes → <em>Recibidos</em>.</li>
              <li>Filtre por rango de fechas y descargue el reporte TXT (o copie las claves de acceso).</li>
              <li>Pegue el contenido en el cuadro de abajo y pulse <em>Detectar claves</em>.</li>
            </ol>
            <p className="text-[12px] text-[#5c7368] mt-2">
              <AlertCircle size={12} className="inline mr-1" />
              Después de importar, solo debe agregar <strong>retención</strong>, <strong>medio de pago</strong> y <strong>descripción</strong>. El resto de datos (proveedor, número, montos, XML) se rellenan automáticamente.
            </p>
          </div>
        </div>
      </div>

      {/* Textarea para pegar */}
      <div className="rounded-md border border-[#d1dbd6] bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-[#15241d]">Contenido con claves de acceso</label>
          {claves.length > 0 && <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">{claves.length} clave(s) detectada(s)</Badge>}
        </div>
        <Textarea
          rows={8}
          value={texto}
          onChange={(e) => { setTexto(e.target.value); setClaves([]); setResult(null); }}
          placeholder="Pegue aquí el TXT del SRI, o las claves de acceso separadas por espacios/comas/líneas...&#10;&#10;Ejemplo:&#10;1002202401099999999900110010010000000011234567810&#10;1002202401099999999900110010010000000021234567811"
          className="font-mono text-xs"
          data-testid="import-sri-textarea"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={parsear} disabled={parsing || !texto.trim()} data-testid="parse-claves-btn">
            <FileText size={14} className="mr-1" /> {parsing ? "Detectando..." : "Detectar claves"}
          </Button>
          <Button onClick={importar} disabled={importing || claves.length === 0} className="bg-[#205c45] hover:bg-[#15241d]" data-testid="import-sri-btn">
            <Download size={14} className="mr-1" /> {importing ? `Importando ${claves.length}...` : `Importar ${claves.length} factura(s)`}
          </Button>
        </div>
      </div>

      {/* Resultado */}
      {result && (
        <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden" data-testid="import-sri-result">
          {result.creadas > 0 && (
            <div className="px-4 py-3 bg-[#e9f2ee] border-b border-[#b2d0c2] text-sm text-[#205c45]">
              <b>✅ {result.creadas} factura(s) importada(s).</b> Están disponibles en{" "}
              <Link to="/compras" className="underline font-semibold hover:text-[#15241d]" data-testid="link-ver-en-compras">
                Compras → Facturas de compra
              </Link>
              {" "}con la etiqueta <span className="px-1.5 py-0.5 rounded bg-[#eef2f9] text-[#2d4a73] border border-[#b2c5db] text-[10px] font-semibold">📥 Importada SRI</span>.
              Puede filtrar por proveedor para encontrarla rápido. Completar retención + pago cuando esté listo.
            </div>
          )}
          <div className="grid grid-cols-3 divide-x border-b">
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold">Creadas</div>
              <div className="text-2xl font-bold text-[#205c45] tabular-nums" data-testid="result-creadas">{result.creadas}</div>
            </div>
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold">Ya existentes</div>
              <div className="text-2xl font-bold text-[#8a5500] tabular-nums" data-testid="result-existentes">{result.existentes}</div>
            </div>
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold">Con errores</div>
              <div className="text-2xl font-bold text-[#b53a25] tabular-nums" data-testid="result-errores">{result.con_errores}</div>
            </div>
          </div>
          <table className="erp-table">
            <thead><tr><th>Clave de acceso</th><th>Resultado</th><th></th></tr></thead>
            <tbody>
              {(result.detalles || []).map((d, i) => (
                <tr key={i} data-testid={`result-row-${i}`}>
                  <td className="font-mono text-[10px] break-all">{d.clave_acceso}</td>
                  <td className={d.ok ? "text-[#205c45]" : d.existente ? "text-[#8a5500]" : "text-[#b53a25]"}>
                    {d.ok ? <CheckCircle2 size={14} className="inline mr-1" /> : d.existente ? <AlertCircle size={14} className="inline mr-1" /> : <XCircle size={14} className="inline mr-1" />}
                    {d.resultado}
                    {d.ok && !d.proveedor_conocido && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]">
                        Proveedor nuevo · vincular manualmente
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    {d.ok && d.id && (
                      <Link to="/compras" className="text-[11px] text-[#205c45] underline hover:text-[#15241d]" title="Ir a Facturas de compra para completar retención y pago">
                        Ver en Facturas →
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
